import { cn } from "@postman/core/cn";
import { cloneElement, isValidElement, useId } from "react";
import type { ComponentProps, ReactElement, ReactNode } from "react";

/**
 * The portal's furniture, in the site's visual language: paper, ink, hairline
 * rules, mono labels. Deliberately small and unabstracted — nine components,
 * no variants system, because the portal is forms and lists and a design
 * system for that is more code than the screens it serves.
 */

export function Button({
  className,
  variant = "primary",
  ...props
}: ComponentProps<"button"> & { variant?: "primary" | "ghost" | "danger" }) {
  return (
    <button
      {...props}
      className={cn(
        "mono inline-flex h-11 items-center justify-center gap-2 px-5 transition-colors",
        "disabled:pointer-events-none disabled:opacity-40",
        variant === "primary" &&
          "bg-ink text-paper hover:bg-[color:var(--accent)]",
        variant === "ghost" &&
          "border border-line text-ink hover:bg-paper-3",
        variant === "danger" &&
          "border border-line text-[#B3261E] hover:bg-[#B3261E] hover:text-paper",
        className,
      )}
    />
  );
}

export function Card({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn("border border-line bg-paper-2 p-6 md:p-8", className)}
    />
  );
}

export function Label({ className, ...props }: ComponentProps<"label">) {
  return <label {...props} className={cn("mono text-ink-3", className)} />;
}

const control =
  "w-full border border-line bg-paper-2 px-3 py-2.5 text-[15px] leading-normal " +
  "placeholder:text-ink-3 focus:border-[color:var(--accent)] focus:outline-none";

export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input {...props} className={cn(control, "h-11", className)} />;
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return <textarea {...props} className={cn(control, "resize-y", className)} />;
}

/* No `Select` here on purpose — see components/pick.tsx. A native `<select>`
   is the one control that cannot be styled to match this page, so the portal
   has its own listbox and this module does not offer the shortcut back. */

/**
 * A label, one control, and the line underneath it.
 *
 * The association is made HERE rather than at the eleven call sites, because
 * a `htmlFor` that has to be remembered every time is a `htmlFor` that will be
 * forgotten — and a label with nothing behind it looks completely correct on
 * screen while announcing nothing at all. The control's own `id` wins if it
 * has one, `htmlFor` next, and a generated one otherwise; help and error text
 * are wired the same way through `aria-describedby`, so the sentence under the
 * field is read out with it instead of being stumbled on afterwards.
 *
 * A group of controls (a set of checkboxes) has no single thing to point at.
 * Those carry their own `role="group"` and label — see `AnswerField`.
 */
export function Field({
  label,
  help,
  error,
  required,
  htmlFor,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  const auto = useId();
  const only = isValidElement(children)
    ? (children as ReactElement<{ id?: string; "aria-describedby"?: string }>)
    : null;
  const id = only?.props.id ?? htmlFor ?? auto;
  const noteId = error || help ? `${id}-note` : undefined;

  const control =
    only && (!only.props.id || noteId)
      ? cloneElement(only, {
          id,
          "aria-describedby": noteId ?? only.props["aria-describedby"],
        })
      : children;

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="text-[color:var(--accent)]"> *</span>
        ) : null}
      </Label>
      {control}
      {error ? (
        <p id={noteId} className="text-[13px] text-[#B3261E]">
          {error}
        </p>
      ) : help ? (
        <p id={noteId} className="text-[13px] text-ink-3">
          {help}
        </p>
      ) : null}
    </div>
  );
}

export function Callout({
  tone = "info",
  children,
}: {
  tone?: "info" | "error";
  children: ReactNode;
}) {
  return (
    <p
      role={tone === "error" ? "alert" : undefined}
      className={cn(
        "border-l-2 py-2 pl-4 text-[14px] leading-relaxed",
        tone === "error"
          ? "border-[#B3261E] text-[#B3261E]"
          : "border-[color:var(--accent)] text-ink-2",
      )}
    >
      {children}
    </p>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="mono text-ink-3">{children}</p>;
}

export function PageTitle({
  eyebrow,
  title,
  lede,
}: {
  eyebrow?: string;
  title: string;
  lede?: string;
}) {
  return (
    <header className="flex flex-col gap-3">
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h1 className="display text-[clamp(30px,5vw,46px)]">{title}</h1>
      {lede ? (
        <p className="max-w-[52ch] text-[16px] leading-relaxed text-ink-2">
          {lede}
        </p>
      ) : null}
    </header>
  );
}

/** The full-page waiting state. One line, no spinner theatre. */
export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="grid min-h-[50vh] place-items-center">
      <p className="mono text-ink-3">{label}…</p>
    </div>
  );
}

/**
 * A state, as a word with a colour.
 *
 * Deliberately not a traffic light: `rejected` is not red and `selected` is
 * not green, because a student reads their own dashboard here and a wall of
 * red is a worse way to learn you did not get in than a sentence. The accent
 * marks what is LIVE; everything settled is ink.
 */
export function Pill({
  tone = "quiet",
  children,
}: {
  tone?: "quiet" | "live" | "good" | "gone" | "warn";
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "mono inline-flex items-center border px-2 py-1",
        tone === "quiet" && "border-line text-ink-3",
        tone === "live" && "border-[color:var(--accent)] text-[color:var(--accent-text)]",
        tone === "good" && "border-ink bg-ink text-paper",
        tone === "gone" && "border-line text-ink-3 line-through",
        tone === "warn" && "border-[#B3261E] text-[#B3261E]",
      )}
    >
      {children}
    </span>
  );
}

/** A hairline-ruled block of rows. The portal's only "table" shape. */
export function Rows({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn("divide-y divide-[color:var(--color-line)] border border-line", className)}
    />
  );
}

/** Empty state: a sentence, not a shrug. */
export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="border border-dashed border-line px-5 py-8 text-[14px] text-ink-3">
      {children}
    </p>
  );
}

/** A labelled checkbox that matches the rest of the controls. */
export function Check({
  label,
  className,
  ...props
}: ComponentProps<"input"> & { label: ReactNode }) {
  return (
    <label className={cn("flex cursor-pointer items-start gap-3 text-[15px]", className)}>
      <input
        {...props}
        type="checkbox"
        className="mt-1 size-4 shrink-0 accent-[color:var(--accent)]"
      />
      <span>{label}</span>
    </label>
  );
}
